/**
 * TryOn — modern, single-question-per-screen wizard with dynamic branching.
 *
 * Selecting "Gowns" surfaces silhouette / neckline / length questions.
 * Selecting "Tuxedos" surfaces fit-preference / lapel — no dress questions.
 * Selecting "Accessories" or "Shoes" trims the flow to only the questions
 * that make sense for that category.
 *
 * Design: full-bleed cream backdrop, magazine typography, framer-motion
 * step transitions, tile-driven options with imagery, cinematic reveal.
 */

import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Check, RotateCcw, Loader2, Crown, Gem, Feather, Star, X } from "lucide-react";
import { Seo } from "@/components/Seo";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { productUrl } from "@/lib/productUrl";
import { track } from "@/lib/analytics";

/* ─────────────────────────────────────────────────────────────────────────
   Question catalog
   Each question opts into the flow with a `when(answers)` predicate.
   `single` means "auto-advance on pick" · `multi` means "Next button".
   ────────────────────────────────────────────────────────────────────── */

const CATEGORY_OPTIONS_FEMALE = [
  { v: "prom-dresses",        label: "Prom Gowns",        sub: "Floor-length couture",   icon: Crown,   },
  { v: "homecoming-dresses",  label: "Homecoming",        sub: "Short & flirty",         icon: Sparkles,},
];

const CATEGORY_OPTIONS_MALE = [
  { v: "tuxedos",             label: "Tuxedos & Suits",   sub: "Custom-fit black tie",   icon: Gem,     },
  { v: "accessories",         label: "Accessories",       sub: "Bow ties, cufflinks +",  icon: Feather, },
  { v: "shoes",               label: "Formal Shoes",      sub: "Oxfords & loafers",      icon: Star,    },
];

const isDress    = (a) => ["prom-dresses","homecoming-dresses","graduation-dresses"].includes(a.category);
const isTuxedo   = (a) => a.category === "tuxedos";
const isAccOrShoe= (a) => a.category === "accessories" || a.category === "shoes";

const QUESTIONS = [
  {
    id: "shopper",
    when: () => true,
    kind: "tiles",
    title: "Who are we shopping for?",
    sub: "So we only surface looks that fit — dresses for her, tuxedos for him.",
    options: [
      { v: "female", label: "For Her", sub: "Prom & homecoming gowns", icon: Sparkles },
      { v: "male",   label: "For Him", sub: "Tuxedos, suits, accessories",    icon: Gem      },
    ],
  },
  {
    id: "category",
    when: (a) => !!a.shopper,
    kind: "tiles",
    title: "What are we shopping for?",
    sub: "This shapes the rest of your flow.",
    options: (a) => (a.shopper === "male" ? CATEGORY_OPTIONS_MALE : CATEGORY_OPTIONS_FEMALE),
  },
  {
    id: "tux_occasion",
    when: (a) => isTuxedo(a),
    kind: "cards",
    title: "The occasion?",
    options: [
      { v: "prom",       label: "Prom",         emoji: "🎩" },
      { v: "homecoming", label: "Homecoming",   emoji: "🎓" },
      { v: "graduation", label: "Graduation",   emoji: "💎" },
      { v: "other",      label: "Another formal", emoji: "🥂" },
    ],
  },
  {
    id: "acc_type",
    when: (a) => a.category === "accessories",
    kind: "cards",
    title: "Which piece?",
    options: [
      { v: "Bow Tie",     label: "Bow tie",     emoji: "🎀" },
      { v: "Necktie",     label: "Necktie",     emoji: "👔" },
      { v: "Cuff Link",   label: "Cuff links",  emoji: "💎" },
      { v: "Pocket Square", label: "Pocket square", emoji: "🟦" },
      { v: "Cummerbund",  label: "Cummerbund",  emoji: "🎗" },
      { v: "any",         label: "Show me all", emoji: "✨" },
    ],
  },
  {
    id: "size",
    when: (a) => !isAccOrShoe(a),
    kind: "pills",
    title: "Your size?",
    sub: isTuxedo({}) ? "Chest / jacket size (US)." : "Dress size (US).",
    options: (a) => isTuxedo(a)
      ? ["34","36","38","40","42","44","46","48","50","52"].map((s) => ({ v: s, label: s + "R" }))
      : ["00","0","2","4","6","8","10","12","14","16","18","20","22","24"].map((s) => ({ v: s, label: s })),
  },
  {
    id: "color",
    when: () => true,
    kind: "swatches",
    multi: true,
    title: "Color story?",
    sub: "Pick one or two shades — we'll style around it.",
    options: [
      { v: "Black",     label: "Black",     swatch: "#0b0b0b" },
      { v: "White",     label: "White",     swatch: "#f8f6ee" },
      { v: "Ivory",     label: "Ivory",     swatch: "#f2ead6" },
      { v: "Champagne", label: "Champagne", swatch: "#e5cfa6" },
      { v: "Blush",     label: "Blush",     swatch: "#f2c9c3" },
      { v: "Pink",      label: "Pink",      swatch: "#e88ba6" },
      { v: "Red",       label: "Red",       swatch: "#b32032" },
      { v: "Burgundy",  label: "Burgundy",  swatch: "#5b1a26" },
      { v: "Coral",     label: "Coral",     swatch: "#e8734a" },
      { v: "Yellow",    label: "Yellow",    swatch: "#e6c65a" },
      { v: "Green",     label: "Green",     swatch: "#5f7d55" },
      { v: "Emerald",   label: "Emerald",   swatch: "#0f7a4f" },
      { v: "Celadon",   label: "Celadon",   swatch: "#a8bfb0" },
      { v: "Teal",      label: "Teal",      swatch: "#1f7f80" },
      { v: "Blue",      label: "Blue",      swatch: "#3568b0" },
      { v: "Navy",      label: "Navy",      swatch: "#1a2b4a" },
      { v: "Royal",     label: "Royal blue",swatch: "#2842b5" },
      { v: "Purple",    label: "Purple",    swatch: "#573282" },
      { v: "Lavender",  label: "Lavender",  swatch: "#b8a4d9" },
      { v: "Silver",    label: "Silver",    swatch: "#c9c9c9" },
      { v: "Gray",      label: "Gray",      swatch: "#7d7d7d" },
      { v: "Gold",      label: "Gold",      swatch: "#c99a4a" },
      { v: "TAN",       label: "Tan",       swatch: "#c9a672" },
      { v: "Brown",     label: "Brown",     swatch: "#6b3e26" },
      { v: "Chocolate", label: "Chocolate", swatch: "#4a2f1f" },
      { v: "Cognac",    label: "Cognac",    swatch: "#8b4513" },
    ],
  },
  {
    id: "silhouette",
    when: (a) => isDress(a),
    kind: "cards",
    multi: true,
    title: "The silhouette that feels like you.",
    sub: "Pick as many as you like — we'll blend them.",
    options: [
      { v: "Ballgown",    label: "Ballgown",    emoji: "👗" },
      { v: "Mermaid",     label: "Mermaid",     emoji: "🧜🏽‍♀️" },
      { v: "A-Line",      label: "A-line",      emoji: "🌷" },
      { v: "Sheath",      label: "Sheath",      emoji: "💫" },
      { v: "Fit & Flare", label: "Fit & flare", emoji: "🌸" },
      { v: "Trumpet",     label: "Trumpet",     emoji: "🎺" },
    ],
  },
  {
    id: "length",
    when: (a) => isDress(a),
    kind: "cards",
    title: "How long?",
    options: [
      { v: "Long",        label: "Floor-length",  emoji: "👑" },
      { v: "Tea Length",  label: "Tea-length",    emoji: "🌸" },
      { v: "Short",       label: "Short & flirty",emoji: "💃" },
      { v: "any",         label: "Show me all",   emoji: "🎀" },
    ],
  },
  {
    id: "neckline",
    when: (a) => isDress(a),
    kind: "cards",
    multi: true,
    title: "Neckline preference?",
    sub: "Multi-select — or skip if you're open.",
    options: [
      { v: "Sweetheart", label: "Sweetheart",   emoji: "💗" },
      { v: "V-Neck",     label: "V-Neck",       emoji: "🔻" },
      { v: "Strapless",  label: "Strapless",    emoji: "✨" },
      { v: "Off-Shoulder", label: "Off-shoulder", emoji: "🌙" },
      { v: "Halter",     label: "Halter",       emoji: "🎗" },
      { v: "One-Shoulder", label: "One-shoulder", emoji: "⚡" },
    ],
  },
  {
    id: "tux_fit",
    when: (a) => isTuxedo(a),
    kind: "cards",
    title: "Preferred fit?",
    options: [
      { v: "slim",     label: "Slim",     emoji: "📐" },
      { v: "modern",   label: "Modern",   emoji: "🖤" },
      { v: "classic",  label: "Classic",  emoji: "🎩" },
      { v: "any",      label: "Not sure — show all", emoji: "🎯" },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────────── */

export default function TryOn() {
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tryOnsLeft, setTryOnsLeft] = useState(null);
  const [heroImgs, setHeroImgs] = useState({}); // {category_slug: url, female_hero:url, male_hero:url}
  const [liveCount, setLiveCount] = useState(null);
  const [prevCount, setPrevCount] = useState(null);
  const [relaxNotice, setRelaxNotice] = useState("");
  const [sneakPeek, setSneakPeek] = useState(null); // { image, name } — the "you're getting closer" thumbnail
  // Cascading step facets — what values are still available for each remaining
  // question, given what the user has already picked. Refetched on each step.
  // `stepFacets === null` means "facets are being fetched right now" — the UI
  // must NOT render any option until fresh facets arrive to prevent the user
  // from tapping a stale option that turns out to have zero inventory.
  const [stepFacets, setStepFacets] = useState(null);
  const [facetsLoading, setFacetsLoading] = useState(false);

  // Determine visible question list for the current answers.
  const visible = useMemo(() => QUESTIONS.filter((q) => q.when(answers)), [answers]);
  const q = visible[step];
  const total = visible.length;
  const done = step >= total;
  const progress = Math.min(100, (step / total) * 100);

  useEffect(() => {
    if (user && api.tryOnCredits) {
      api.tryOnCredits().then(({ data }) => setTryOnsLeft(data.credits)).catch(() => {});
    }
  }, [user]);

  // Fetch real hero images from the boutique catalog (one representative product
  // per category) so the tiles show real inventory instead of stock photos.
  useEffect(() => {
    api.tryonHeroImages()
      .then(({ data }) => {
        const map = {};
        Object.entries(data || {}).forEach(([k, v]) => { if (v && v.image) map[k] = v.image; });
        setHeroImgs(map);
      })
      .catch(() => setHeroImgs({}));
  }, []);

  // Build the current filter set from answers. Only applies filters that
  // actually narrow the catalog — never sends empty arrays or `any` sentinels.
  const buildFilters = (a) => {
    const p = {};
    if (a.category)                          p.category_slug = a.category;
    if ((a.color || []).length)              p.color = a.color.join(",");
    if ((a.silhouette || []).length)         p.silhouette = a.silhouette.join(",");
    if ((a.neckline || []).length)           p.neckline = a.neckline.join(",");
    if (a.length && a.length !== "any")      p.length = a.length;
    if (a.size)                              p.size = a.size;
    return p;
  };

  // Live match count + sneak-peek thumbnail — refetches after every answer change on a debounce.
  useEffect(() => {
    if (!answers.category) { setLiveCount(null); setSneakPeek(null); return; }
    const filters = buildFilters(answers);
    let cancel = false;
    const t = setTimeout(() => {
      // Count
      api.productsCount(filters)
        .then(({ data }) => {
          if (cancel) return;
          setPrevCount(liveCount);
          setLiveCount(data.total);
        })
        .catch(() => { if (!cancel) setLiveCount(null); });
      // Sneak-peek thumbnail — pull 1 real product matching the current filters
      api.products({ ...filters, limit: 1 })
        .then(({ data }) => {
          if (cancel) return;
          const items = Array.isArray(data) ? data : (data.items || []);
          if (items[0] && items[0].images?.[0]) {
            setSneakPeek({ image: items[0].images[0], name: items[0].name });
          }
        })
        .catch(() => { /* silent */ });
    }, 120);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(answers)]);

  // Fetch cascading facets — given all CURRENT answers except the one for the
  // current step, what values are still stocked? Those become the ONLY options
  // shown at this step. Never a dead-end pick.
  useEffect(() => {
    if (!q || !answers.category) { setStepFacets(null); setFacetsLoading(false); return; }
    // CRITICAL: clear stale facets from the previous step BEFORE fetching so
    // the UI can't briefly render options that don't apply to the new scope.
    setStepFacets(null);
    setFacetsLoading(true);
    const trimmed = { ...answers };
    delete trimmed[q.id]; // exclude the current step's own selection
    const params = buildFilters(trimmed);
    let cancel = false;
    api.productFacets(params)
      .then(({ data }) => {
        if (cancel) return;
        setStepFacets({
          color:      new Set((data.color      || []).map((x) => (x.value || "").toLowerCase())),
          silhouette: new Set((data.silhouette || []).map((x) => x.value)),
          neckline:   new Set((data.neckline   || []).map((x) => x.value)),
          length:     new Set((data.length     || []).map((x) => x.value)),
          sleeve:     new Set((data.sleeve     || []).map((x) => x.value)),
          size:       new Set((data.size       || []).map((x) => x.value)),
        });
      })
      .catch(() => { if (!cancel) setStepFacets({}); })
      .finally(() => { if (!cancel) setFacetsLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.id, JSON.stringify(answers)]);

  // Resolve dynamic option lists, injecting real product hero images.
  // Cascading facets — hide any option that has zero remaining inventory given
  // the current answers. Guarantees no dead-end pick.
  //
  // If facets are still loading (stepFacets === null) we return an EMPTY list
  // for every filter-gated question, forcing the UI to render a "checking
  // availability" loader instead of stale options.
  const resolvedOptions = useMemo(() => {
    if (!q) return [];
    const raw = typeof q.options === "function" ? q.options(answers) : q.options;
    if (q.id === "shopper") {
      return raw.map((o) => ({ ...o, img: o.v === "male" ? heroImgs.male_hero : heroImgs.female_hero }));
    }
    if (q.id === "category") {
      return raw.map((o) => ({ ...o, img: heroImgs[o.v] }));
    }
    // These questions aren't gated by facets (they're driven by the branch tree).
    if (["tux_occasion", "tux_fit", "acc_type"].includes(q.id)) {
      return raw;
    }
    // Everything else IS facet-gated. If facets haven't loaded, return nothing
    // — the UI shows a loader in that case.
    if (!stepFacets) return [];
    const gate = (facetSet, val, caseFold = false) => {
      if (!facetSet) return false;
      const v = caseFold ? String(val).toLowerCase() : val;
      if (facetSet.has(v)) return true;
      if (caseFold) {
        for (const f of facetSet) {
          if (f === v || f.includes(v) || v.includes(f)) return true;
        }
      }
      return false;
    };
    if (q.id === "color")      return raw.filter((o) => gate(stepFacets.color, o.v, true));
    if (q.id === "silhouette") return raw.filter((o) => gate(stepFacets.silhouette, o.v));
    if (q.id === "neckline")   return raw.filter((o) => gate(stepFacets.neckline, o.v));
    if (q.id === "length") {
      // Only include "any" if there's more than one real length value in scope,
      // so the escape hatch never lands the user on a filter with 0 results.
      const opts = raw.filter((o) => o.v !== "any" && gate(stepFacets.length, o.v));
      if (opts.length > 1) opts.push(raw.find((o) => o.v === "any"));
      return opts;
    }
    if (q.id === "size")       return raw.filter((o) => gate(stepFacets.size, o.v));
    return raw;
  }, [q, answers, heroImgs, stepFacets]);

  const currentSel = q ? (Array.isArray(answers[q.id]) ? answers[q.id] : []) : [];
  const currentSingleSel = q ? answers[q.id] : null;

  const pick = (val) => {
    if (!q) return;
    if (q.multi) {
      const cur = answers[q.id] || [];
      const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      setAnswers({ ...answers, [q.id]: next });
      return;
    }
    // Single-select: set + auto-advance.
    const nextAnswers = { ...answers, [q.id]: val };
    setAnswers(nextAnswers);
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const advance = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));
  const reset = () => { setAnswers({}); setStep(0); setResults([]); };

  // Auto-submit the moment the user finishes the last step (no "Reveal" button).
  useEffect(() => {
    if (done && !loading && results.length === 0) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const submit = async () => {
    setLoading(true);
    setRelaxNotice("");
    try {
      // Cascading facets guarantee that every clickable filter has inventory,
      // so the strict query MUST return results. If it somehow doesn't (data
      // race, network hiccup), fall back to just the category.
      const base = buildFilters(answers);
      let items = [];
      const { data } = await api.products({ limit: 24, ...base });
      items = Array.isArray(data) ? data : (data.items || []);
      if (items.length === 0 && answers.category) {
        const fb = await api.products({ limit: 24, category_slug: answers.category });
        items = Array.isArray(fb.data) ? fb.data : (fb.data.items || []);
      }
      setResults(items);
      track("tryon_wizard_completed", {
        category: answers.category,
        result_count: items.length,
      });
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };

  const canAdvance = q?.multi ? currentSel.length > 0 : currentSingleSel != null;

  // Auto-skip a step if cascading facets left it with no valid options.
  // This should be rare but handles edge combinations gracefully.
  useEffect(() => {
    if (!q || done || results.length > 0) return;
    if (!stepFacets) return; // wait for facets to load
    // shopper/category never gate on facets — always have options
    if (q.id === "shopper" || q.id === "category" ||
        q.id === "tux_occasion" || q.id === "tux_fit" || q.id === "acc_type") return;
    if (resolvedOptions.length === 0) {
      // Nothing to pick — skip forward.
      setStep((s) => s + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.id, resolvedOptions.length, stepFacets]);

  // Human-readable label for a single answer key/value pair.
  const _labelFor = (key, val) => {
    if (key === "category") {
      const all = [...CATEGORY_OPTIONS_FEMALE, ...CATEGORY_OPTIONS_MALE];
      return all.find((c) => c.v === val)?.label || val;
    }
    if (key === "shopper")     return val === "male" ? "For Him" : "For Her";
    if (key === "tux_fit")     return `${val[0].toUpperCase()}${val.slice(1)} fit`;
    if (key === "tux_occasion" || key === "occasion") return val[0].toUpperCase() + val.slice(1);
    if (key === "length" && val === "any") return "Any length";
    return String(val);
  };

  // Flatten answers into an ordered list of removable chips (shopper hidden).
  const breadcrumbs = useMemo(() => {
    const chips = [];
    const push = (key, val) => chips.push({ key, val, label: _labelFor(key, val) });
    const keysInOrder = ["category", "tux_occasion", "silhouette", "length", "neckline", "tux_fit", "acc_type", "color", "size"];
    for (const k of keysInOrder) {
      const v = answers[k];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      if (Array.isArray(v)) v.forEach((x) => push(k, x));
      else push(k, v);
    }
    return chips;
  }, [answers]);

  const removeChip = (chip) => {
    setAnswers((prev) => {
      const cur = prev[chip.key];
      if (Array.isArray(cur)) {
        return { ...prev, [chip.key]: cur.filter((x) => x !== chip.val) };
      }
      const next = { ...prev };
      delete next[chip.key];
      return next;
    });
    // If we're currently past the removed step, snap the wizard back to that step.
    const removedIdx = visible.findIndex((qq) => qq.id === chip.key);
    if (removedIdx >= 0 && removedIdx < step) setStep(removedIdx);
  };

  // Pick the specific variant image + label matching the user's color choice.
  // If the user picked "Gold", show the Gold variant image (not the default which
  // might be a Bright Royal). Prefers EXACT matches ("Gold") over compound matches
  // ("Black/Gold") so labels stay honest.
  const pickedColors = (answers.color || []).map((c) => c.toLowerCase());
  const bestVariantFor = (product) => {
    if (!pickedColors.length || !Array.isArray(product.variants)) {
      return { image: product.images?.[0], label: product.color || product.variants?.[0]?.color || "" };
    }
    // 1) exact match — e.g., variant.color === "Gold"
    let match = product.variants.find((v) => {
      const c = (v.color || "").toLowerCase();
      return pickedColors.some((p) => c === p);
    });
    // 2) fall back to substring — e.g., "Black/Gold" contains "gold"
    if (!match) {
      match = product.variants.find((v) => {
        const c = (v.color || "").toLowerCase();
        return pickedColors.some((p) => c.includes(p));
      });
    }
    if (match && match.image) return { image: match.image, label: match.color };
    if (match) return { image: product.images?.[0], label: match.color };
    return { image: product.images?.[0], label: product.color || product.variants?.[0]?.color || "" };
  };

  /* ─── Render ───────────────────────────────────────────────────────── */
  return (
    <div data-testid="try-on-page" className="min-h-screen bg-cream relative overflow-hidden">
      <Seo title="Virtual Try-On — Heather's Boutique" description="Answer a few questions, see your matches, then try them on with AI." path="/try-on" />

      {/* Ambient blur backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full bg-blush/60 blur-3xl" />
        <div className="absolute top-40 -right-32 w-[500px] h-[500px] rounded-full bg-champagne/50 blur-3xl" />
      </div>

      <section className="relative z-10 max-w-[1000px] mx-auto px-5 sm:px-8 pt-16 pb-24">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="font-label text-[10px] text-rosemetal tracking-[0.3em]">VIRTUAL TRY-ON</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-[0.95]">
            See yourself in it<br /><span className="italic text-rosemetal">before you commit.</span>
          </h1>
          {tryOnsLeft !== null && (
            <div className="inline-flex items-center gap-2 mt-6 border border-border rounded-full px-4 py-1.5 bg-ivory/60 backdrop-blur">
              <Sparkles className="w-3 h-3 text-rosemetal" />
              <span className="font-label text-[10px] tracking-widest">{tryOnsLeft} TRY-ONS LEFT</span>
              <Link to="/try-on/pricing" className="font-label text-[10px] text-rosemetal underline underline-offset-4">ADD MORE</Link>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {!done && !results.length && (
          <div className="mb-6">
            <div className="flex justify-between font-label text-[9px] text-muted-foreground tracking-[0.25em] mb-2">
              <span>STEP {Math.min(step + 1, total)} OF {total}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-[3px] bg-blush/50 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-rosemetal"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
          </div>
        )}

        {/* Live match count + breadcrumb chips */}
        {!results.length && (breadcrumbs.length > 0 || liveCount !== null) && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex flex-wrap items-center gap-2 justify-center"
            data-testid="wizard-breadcrumbs"
          >
            {liveCount !== null && (
              <motion.div
                key={liveCount}
                initial={{ scale: 0.85, opacity: 0.7 }}
                animate={{ scale: [0.85, 1.08, 1], opacity: 1 }}
                transition={{ duration: 0.45, times: [0, 0.6, 1] }}
                className="bg-ink text-ivory rounded-full px-4 py-1.5 font-label text-[10px] tracking-[0.25em] flex items-center gap-2 shadow-lg"
              >
                <Sparkles className="w-3 h-3 text-champagne" />
                <span data-testid="wizard-count" className="font-display text-sm tracking-normal">{liveCount.toLocaleString()}</span>
                <span className="opacity-80">{liveCount === 1 ? "look" : "looks"} match</span>
              </motion.div>
            )}
            {breadcrumbs.map((c, i) => (
              <button
                key={`${c.key}-${c.val}-${i}`}
                onClick={() => removeChip(c)}
                data-testid={`chip-${c.key}-${c.val}`}
                className="inline-flex items-center gap-1.5 bg-ivory border border-border hover:border-rosemetal text-ink rounded-full px-3 py-1.5 font-label text-[10px] tracking-[0.15em] transition-colors group"
              >
                {c.label}
                <X className="w-3 h-3 opacity-50 group-hover:opacity-100" />
              </button>
            ))}
          </motion.div>
        )}

        {/* Sneak-peek thumbnail — floating card that swaps in a real product image
            matching the current filter set, so students see themselves getting closer. */}
        <AnimatePresence>
          {!results.length && sneakPeek?.image && liveCount !== null && liveCount > 0 && (
            <motion.div
              key={sneakPeek.image}
              initial={{ opacity: 0, x: 30, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.5 }}
              className="hidden lg:block fixed bottom-8 right-8 w-[220px] bg-ivory rounded-md shadow-2xl border border-border p-3 z-30"
              data-testid="wizard-sneak-peek"
            >
              <p className="font-label text-[9px] text-rosemetal tracking-[0.3em] mb-2">✦ ONE OF YOUR MATCHES</p>
              <div className="aspect-[3/4] overflow-hidden rounded-sm bg-blush">
                <motion.img
                  key={sneakPeek.image}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7 }}
                  src={sneakPeek.image}
                  alt={sneakPeek.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="font-body text-xs text-muted-foreground mt-2 line-clamp-1">{sneakPeek.name}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Steps */}
        <AnimatePresence mode="wait">
          {!done && q && !results.length && (
            <motion.div
              key={q.id + step}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
              data-testid={`step-${q.id}`}
            >
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="font-display text-4xl sm:text-5xl leading-tight">{q.title}</h2>
                {q.sub && <p className="font-body text-sm text-muted-foreground mt-3">{q.sub}</p>}
              </div>

              {/* Renderer by kind */}
              <div className="mt-10">
                {/* Loading state — shown while cascading facets are being fetched
                    so the user can't tap a stale option that has zero inventory. */}
                {facetsLoading && !["shopper","category","tux_occasion","tux_fit","acc_type"].includes(q.id) && (
                  <div className="text-center py-16" data-testid="wizard-facets-loading">
                    <div className="inline-block w-8 h-8 border-2 border-rosemetal border-t-transparent rounded-full animate-spin" />
                    <p className="font-label text-[10px] text-muted-foreground tracking-[0.3em] mt-4">CHECKING AVAILABILITY…</p>
                  </div>
                )}

                {!facetsLoading && q.kind === "tiles" && (
                  <div className={`grid gap-4 max-w-[900px] mx-auto ${resolvedOptions.length <= 2 ? "sm:grid-cols-2" : resolvedOptions.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
                    {resolvedOptions.map((o, idx) => {
                      const Icon = o.icon || Sparkles;
                      const active = currentSingleSel === o.v;
                      return (
                        <motion.button
                          key={o.v}
                          data-testid={`tile-${o.v}`}
                          onClick={() => pick(o.v)}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35, delay: idx * 0.06 }}
                          whileHover={{ y: -4 }}
                          className={`group relative overflow-hidden rounded-md text-left transition-shadow ${active ? "ring-2 ring-rosemetal shadow-2xl" : "shadow-sm hover:shadow-xl"}`}
                        >
                          <div className="aspect-[4/5] bg-gradient-to-br from-blush to-champagne overflow-hidden">
                            {o.img && <img src={o.img} alt={o.label} className="w-full h-full object-cover object-top transition-transform duration-[900ms] group-hover:scale-110" />}
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/25 to-transparent" />
                          </div>
                          <div className="absolute inset-x-0 bottom-0 p-5 text-ivory">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="w-4 h-4" strokeWidth={1.5} />
                              <div className="font-display text-2xl leading-none">{o.label}</div>
                            </div>
                            {o.sub && <div className="font-label text-[10px] tracking-[0.2em] opacity-80">{o.sub}</div>}
                          </div>
                          {active && (
                            <motion.div
                              initial={{ scale: 0, rotate: -30 }}
                              animate={{ scale: 1, rotate: 0 }}
                              className="absolute top-3 right-3 bg-rosemetal text-ivory rounded-full w-9 h-9 flex items-center justify-center shadow-lg"
                            >
                              <Check className="w-4 h-4" />
                            </motion.div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                )}

                {!facetsLoading && q.kind === "cards" && (
                  <div className={`grid gap-3 ${resolvedOptions.length > 4 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                    {resolvedOptions.map((o) => {
                      const active = q.multi ? currentSel.includes(o.v) : currentSingleSel === o.v;
                      return (
                        <button
                          key={o.label}
                          data-testid={`card-${q.id}-${o.label}`}
                          onClick={() => pick(o.v)}
                          className={`text-left rounded-md p-5 flex items-center gap-4 transition-all ${active ? "bg-ink text-ivory shadow-lg scale-[1.02]" : "bg-ivory/90 backdrop-blur hover:bg-ivory hover:shadow-md border border-border"}`}
                        >
                          <span className="text-2xl">{o.emoji}</span>
                          <div className="flex-1 font-display text-lg leading-tight">{o.label}</div>
                          {active && <Check className="w-4 h-4 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {!facetsLoading && q.kind === "swatches" && (
                  <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-7 gap-3">
                    {resolvedOptions.map((o) => {
                      const active = currentSel.includes(o.v);
                      return (
                        <button
                          key={o.v}
                          data-testid={`swatch-${o.v}`}
                          onClick={() => pick(o.v)}
                          className={`group flex flex-col items-center gap-2 p-2 rounded-md transition-all ${active ? "bg-ink text-ivory" : "hover:bg-ivory/80"}`}
                        >
                          <div className={`w-14 h-14 rounded-full border transition-all ${active ? "ring-2 ring-rosemetal ring-offset-2 ring-offset-ink" : "border-border group-hover:scale-110"}`} style={{ backgroundColor: o.swatch }} />
                          <div className={`font-label text-[9px] tracking-widest ${active ? "text-ivory" : "text-ink"}`}>{o.label.toUpperCase()}</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {!facetsLoading && q.kind === "pills" && (
                  <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                    {resolvedOptions.map((o) => {
                      const active = currentSingleSel === o.v;
                      return (
                        <button
                          key={o.v}
                          data-testid={`pill-${o.v}`}
                          onClick={() => pick(o.v)}
                          className={`px-5 py-2.5 rounded-full font-body text-sm border transition-all ${active ? "bg-ink text-ivory border-ink" : "bg-ivory text-ink border-border hover:border-ink"}`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Nav */}
              <div className="mt-10 flex items-center justify-between max-w-2xl mx-auto">
                <button
                  onClick={back}
                  disabled={step === 0}
                  className="font-label text-[10px] text-muted-foreground tracking-[0.25em] flex items-center gap-2 disabled:opacity-30 hover:text-ink"
                >
                  <ArrowLeft className="w-3 h-3" /> BACK
                </button>
                {q.multi ? (
                  <button
                    onClick={advance}
                    disabled={!canAdvance || facetsLoading}
                    data-testid="wizard-next"
                    className={`inline-flex items-center gap-2 px-7 py-3 rounded-full font-label text-[10px] tracking-[0.25em] transition-colors ${canAdvance && !facetsLoading ? "bg-ink text-ivory hover:bg-rosemetal" : "bg-border text-muted-foreground cursor-not-allowed"}`}
                  >
                    NEXT <ArrowRight className="w-3 h-3" />
                  </button>
                ) : (
                  <span className="font-label text-[10px] text-muted-foreground tracking-[0.25em] hidden sm:inline">
                    {facetsLoading ? "CHECKING AVAILABILITY…" : "TAP TO CONTINUE"}
                  </span>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading state while auto-submitting once wizard completes */}
        {done && loading && results.length === 0 && (
          <div className="text-center py-20" data-testid="wizard-loading">
            <div className="inline-block w-12 h-12 border-2 border-rosemetal border-t-transparent rounded-full animate-spin" />
            <p className="font-label text-[10px] text-muted-foreground tracking-[0.3em] mt-5">CURATING YOUR MATCHES…</p>
          </div>
        )}

        {loading && !done && (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-2 border-rosemetal border-t-transparent rounded-full animate-spin" />
            <p className="font-label text-[10px] text-muted-foreground tracking-[0.3em] mt-5">FINDING YOUR LOOKS…</p>
          </div>
        )}

        {/* No exact match — actionable empty state.
            Never a dead end: every filter chip becomes a "REMOVE" button, and
            the user can drop one preference to widen the search themselves. */}
        {done && !loading && results.length === 0 && (
          <div className="text-center py-14 max-w-2xl mx-auto" data-testid="wizard-empty-state">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <p className="font-label text-[10px] text-rosemetal tracking-[0.3em]">SO CLOSE</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[0.95]">
                Nothing this exact <span className="italic text-rosemetal">in stock.</span>
              </h2>
              <p className="font-body text-sm text-muted-foreground mt-4">
                We don't stock a dress matching every one of your picks right now. Tap any preference below to remove it and see options.
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {breadcrumbs.filter((c) => c.key !== "category" && c.key !== "shopper").map((c, i) => (
                  <button
                    key={`empty-chip-${c.key}-${c.val}-${i}`}
                    onClick={() => { removeChip(c); setResults([]); setRelaxNotice(""); }}
                    data-testid={`empty-chip-${c.key}-${c.val}`}
                    className="inline-flex items-center gap-1.5 border border-rosemetal text-ink hover:bg-rosemetal hover:text-ivory rounded-full px-4 py-2 font-label text-[10px] tracking-[0.15em] transition-colors"
                  >
                    REMOVE: {c.label}
                    <X className="w-3 h-3" />
                  </button>
                ))}
              </div>

              <div className="mt-10 flex items-center justify-center gap-3">
                <button
                  onClick={submit}
                  data-testid="wizard-empty-retry"
                  className="inline-flex items-center gap-2 border border-ink px-6 py-3 rounded-full font-label text-[10px] tracking-[0.3em] hover:bg-ink hover:text-ivory transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> RETRY WITH CURRENT PICKS
                </button>
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-2 bg-ink text-ivory px-6 py-3 rounded-full font-label text-[10px] tracking-[0.3em] hover:bg-rosemetal transition-colors"
                >
                  START OVER
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div data-testid="wizard-results" className="mt-2">
            <div className="text-center mb-10">
              <p className="font-label text-[10px] text-rosemetal tracking-[0.3em]">YOUR MATCHES</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[0.95]">
                {results.length} {results.length === 1 ? "look" : "looks"} <span className="italic text-rosemetal">for you.</span>
              </h2>
              <p className="font-body text-sm text-muted-foreground mt-3 max-w-md mx-auto">
                Tap "Try This Look" to see yourself in it with AI, or open the details to lock the size.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {results.map((p, i) => {
                const best = bestVariantFor(p);
                const pdpUrl = productUrl(p, best.label);
                return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.05, 0.4) }}
                  data-testid={`wizard-match-${i}`}
                >
                  <Link to={pdpUrl} className="block relative overflow-hidden bg-blush aspect-[3/4] group rounded-sm">
                    <img src={best.image} alt={p.name} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                  </Link>
                  <div className="mt-3">
                    <div className="font-display text-base leading-tight line-clamp-1">{p.name}</div>
                    <div className="font-label text-[10px] text-muted-foreground mt-1 tracking-widest">
                      {best.label}{p.price ? ` · $${p.price}` : ""}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Link
                        to={`${pdpUrl}?tryon=1`}
                        className="flex-1 text-center bg-ink text-ivory px-2 py-2 font-label text-[9px] rounded-full tracking-widest hover:bg-rosemetal transition-colors"
                      >
                        TRY THIS LOOK
                      </Link>
                      <Link
                        to={pdpUrl}
                        className="border border-ink text-ink px-2 py-2 font-label text-[9px] rounded-full tracking-widest hover:bg-ink hover:text-ivory transition-colors"
                      >
                        VIEW
                      </Link>
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </div>

            <div className="mt-14 text-center">
              <button
                onClick={reset}
                data-testid="wizard-reset"
                className="inline-flex items-center gap-2 border border-ink px-6 py-3 rounded-full font-label text-[10px] tracking-[0.3em] hover:bg-ink hover:text-ivory transition-colors"
              >
                <RotateCcw className="w-3 h-3" /> START OVER
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
